import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Logger } from "@/lib/utils/logger";
import { useTranslations } from "@/i18n/hooks";
import { FloatingLanguageSelector } from "@/components/FloatingLanguageSelector";

const logger = new Logger('CoachResetPassword');

export function CoachResetPassword() {
  const t = useTranslations('coach');
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if we have a hash in the URL (indicates we're in a password reset flow)
  useEffect(() => {
    const checkResetFlow = async () => {
      try {
        // Get current URL hash (hash contains the access token after # fragment)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        // If this isn't a recovery flow or we don't have an access token, redirect
        if (!accessToken || type !== 'recovery') {
          // Check if we have a session as a fallback
          const { data, error } = await supabase.auth.getSession();
          
          if (error || !data.session) {
            logger.warn('No valid recovery tokens or session found', { type, error });
            navigate('/coach-forgot-password', { replace: true });
            return;
          }
          
          // We have a session but no recovery flow - probably already logged in
          logger.info('User already has a session, no recovery flow needed');
          return;
        }
        
        logger.info('Valid recovery flow detected', { type });
        
        // If we have tokens from URL but no session yet, try to exchange tokens for session
        if (accessToken && type === 'recovery') {
          // Clear URL hash for security
          window.history.replaceState(null, '', window.location.pathname);
        }
      } catch (err) {
        logger.error('Unexpected error in reset flow check', { error: err });
        setError(t('resetPassword.errors.unexpected'));
      }
    };
    
    checkResetFlow();
  }, [navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error state
    setError(null);
    
    // Validate passwords
    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordMismatch'));
      return;
    }
    
    // Password complexity requirements
    if (password.length < 8) {
      setError(t('resetPassword.errors.passwordLength'));
      return;
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      setError(t('resetPassword.errors.passwordUppercase') || 'Password must contain at least one uppercase letter');
      return;
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      setError(t('resetPassword.errors.passwordLowercase') || 'Password must contain at least one lowercase letter');
      return;
    }
    
    // Check for at least one number
    if (!/[0-9]/.test(password)) {
      setError(t('resetPassword.errors.passwordNumber') || 'Password must contain at least one number');
      return;
    }
    
    // Check for at least one special character
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError(t('resetPassword.errors.passwordSpecial') || 'Password must contain at least one special character');
      return;
    }
    
    setLoading(true);
    
    try {
      logger.info('Attempting to update password');
      
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        logger.error('Failed to update password', { error });
        setError(error.message || t('resetPassword.errors.updateFailed'));
        return;
      }
      
      logger.info('Password updated successfully');
      setSuccess(true);
      
      toast({
        title: t('resetPassword.toast.successTitle'),
        description: t('resetPassword.toast.successDescription'),
      });
      
      // Redirect to sign in after a delay
      setTimeout(() => {
        navigate('/coach-signin', { replace: true });
      }, 3000);
    } catch (err) {
      logger.error('Unexpected error updating password', { error: err });
      setError(t('resetPassword.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="flex justify-end">
        <FloatingLanguageSelector />
      </div>
      
      <div className="max-w-md mx-auto space-y-6 mt-12">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">{t('resetPassword.title')}</h1>
          <p className="text-gray-400">
            {t('resetPassword.subtitle')}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="bg-red-900/50 border-red-600">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success ? (
          <Alert className="bg-green-900/50 border-green-600">
            <AlertDescription>
              {t('resetPassword.successMessage')}
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="password" className="block">
                {t('resetPassword.newPasswordLabel')}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-gray-900 border-gray-800 text-white"
                placeholder="••••••••"
                disabled={loading}
                minLength={8}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block">
                {t('resetPassword.confirmPasswordLabel')}
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="bg-gray-900 border-gray-800 text-white"
                placeholder="••••••••"
                disabled={loading}
                minLength={8}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? t('resetPassword.updatingButton') : t('resetPassword.updateButton')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
